import {makeTrackEvent} from './makeTrackEvent';

/**
 * Interactions with the Weave and CES eval results tables.
 *
 * Each host declares `surface` so the shared interaction event distinguishes
 * the compare page, the Weave panel, and the CES panel.
 *
 * Scope covers what config-based tracking cannot see. Sort, pagination, and row
 * clicks never persist, and the compare page persists nothing at all.
 */

export type EvalTableSurface =
  | 'eval-compare-page'
  | 'eval-tables-panel'
  | 'ces-eval-tables-panel';

export type EvalTableAction =
  | 'table_viewed'
  | 'sort_changed'
  | 'page_changed'
  | 'page_size_changed'
  | 'filter_changed'
  | 'column_visibility_changed'
  | 'row_opened_detail';

export type EvalTableFieldKind = 'input' | 'output' | 'score';

export type EvalTableInteractionPayload = {
  surface: EvalTableSurface;
  action: EvalTableAction;
  entity: string;
  project: string;
  numEvals: number;
  numRows: number;
  pivot?: 'rows' | 'columns';
  fieldKind?: EvalTableFieldKind;
  direction?: 'asc' | 'desc';
  page?: number;
  pageDirection?: 'next' | 'prev' | 'jump';
  pageSize?: number;
  numHidden?: number;
  via?: 'row' | 'header_button';
};

export const trackEvalTableInteraction = makeTrackEvent<
  EvalTableInteractionPayload,
  {
    surface: {
      description: 'which host rendered the table';
      exampleValues: [
        'eval-compare-page',
        'eval-tables-panel',
        'ces-eval-tables-panel',
      ];
    };
    action: {
      description: 'which eval-table interaction the user performed';
      exampleValues: ['table_viewed', 'sort_changed', 'row_opened_detail'];
    };
    entity: {
      description: 'entity (team or user) owning the evals being compared';
      exampleValues: ['my-team', 'my-user'];
    };
    project: {
      description: 'project the evals belong to';
      exampleValues: ['my-project'];
    };
    numEvals: {
      description: 'how many eval runs are being compared, for segmenting by comparison width';
      exampleValues: [1, 4];
    };
    numRows: {
      description: 'total dataset rows behind the table, for segmenting by dataset size';
      exampleValues: [50, 1200];
    };
    pivot: {
      description: 'whether eval runs render as rows or as columns';
      exampleValues: ['rows', 'columns'];
    };
    fieldKind: {
      description: 'category of the column acted on; never the column name';
      exampleValues: ['input', 'output', 'score'];
    };
    direction: {
      description: 'sort direction applied';
      exampleValues: ['asc', 'desc'];
    };
    page: {
      description: '0-based page index the user moved to';
      exampleValues: [0, 3];
    };
    pageDirection: {
      description: 'how the user moved between pages';
      exampleValues: ['next', 'prev', 'jump'];
    };
    pageSize: {
      description: 'rows per page, logical rather than scaled by numEvals in the runs-as-rows pivot';
      exampleValues: [50, 100];
    };
    numHidden: {
      description: 'columns explicitly hidden after the change; columns absent from the model are visible by default';
      exampleValues: [0, 4];
    };
    via: {
      description: 'which affordance opened the detail view';
      exampleValues: ['row', 'header_button'];
    };
    _description: 'User interactions with the eval results table, on both the Weave compare page and the workspace Evaluation Tables panel';
    _location: 'Weave > Compare evaluations > Results tab; Workspaces > Evaluation Tables panel';
    _motivation: 'Measure whether users actually engage with the table rather than only viewing it, and which affordances they reach for';
  }
>('Weave Evaluation Table Interaction');
